<template>
    <v-card-text>
        <v-card>
            <v-card-title>
                <v-row no-gutters>
                    Treatments
                    <v-spacer/>
                    <treatments-file-upload-form
                        v-if="isTreatmentsFileUploadFormVisible"
                        v-model="treatmentsFile"
                        layout-orientation="horizontal"
                        :experiment-code="experimentCode"
                        @fileSubmitted="saveTreatmentsSetupFile"
                        @onCancel="closeFileUpload"
                    />
                    <v-col v-else-if="userCanCreateTreatments || userCanDeleteTreatments">
                        <v-row no-gutters>
                            <v-spacer/>
                            <v-btn
                                v-if="userCanDeleteTreatments"
                                variant="text"
                                color="error"
                                append-icon="$delete"
                                class="me-4"
                                @click="deleteAllTreatments"
                            >
                                Delete All
                            </v-btn>
                            <v-menu v-if="userCanCreateTreatments">
                                <template v-slot:activator="{ props }">
                                    <v-btn
                                        v-bind="props"
                                        variant="elevated"
                                        color="primary"
                                    >Add Treatments</v-btn>
                                </template>

                                <v-list>
                                    <v-list-item
                                        title="Import Treatments File"
                                        @click="showTreatmentsFileUploadForm"
                                    ></v-list-item>
                                    <v-list-item
                                        title="Add Manually"
                                        @click="addTreatment"
                                    ></v-list-item>
                                </v-list>
                            </v-menu>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text>
                <v-data-table :headers="tableHeaders" :items="treatments" :row-props="getRowProps">
                    <template v-slot:item.actives="{ item }">
                        <v-container align="start" class="ma-1 pa-0">
                            <treatment-component-chip
                                v-if="item.actives.length > 0 && treatmentFormConfig"
                                v-for="active of item.actives"
                                :treatment-component="active"
                                :experiment-setup-status="experimentSetupStatus"
                                :registry-entries-for-lookups="treatmentFormConfig"
                                @onClick="editTreatmentComponent(active)"
                            />
                        </v-container>
                    </template>
                    <template v-slot:item.inactives="{ item }">
                        <v-container align="start" class="ma-1 pa-0">
                            <treatment-component-chip
                                v-if="item.inactives.length > 0 && treatmentFormConfig"
                                v-for="inactive of item.inactives"
                                :treatment-component="inactive"
                                :experiment-setup-status="experimentSetupStatus"
                                :registry-entries-for-lookups="treatmentFormConfig"
                                @onClick="editTreatmentComponent(inactive)"
                            />
                        </v-container>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-row no-gutters>
                            <v-col cols="6">
                                <v-icon-btn
                                    v-if="userCanEditTreatments"
                                    color="primary"
                                    @click="editTreatment(item)"> <v-icon>$pencil</v-icon> </v-icon-btn>
                            </v-col>
                            <v-col cols="6">
                                <v-icon-btn
                                    v-if="userCanDeleteTreatments"
                                    color="error"
                                    @click="deleteTreatment(item)"> <v-icon>$delete</v-icon> </v-icon-btn>
                            </v-col>
                        </v-row>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </v-card-text>
    <v-dialog v-model="editTreatmentComponentDialogVisible" width="50%">
        <treatment-component-form
            :treatment-component="selectedTreatmentComponent!"
            @onSave="saveTreatmentComponent"
            variant="card"
        />
    </v-dialog>
    <v-dialog v-model="editTreatmentDialogVisible" width="60%">
        <treatment-form
            :experiment-setup-status="experimentSetupStatus"
            :form-config="treatmentFormConfig"
            :treatment="selectedTreatment!"
            :existing-treatments-count="treatments.length"
            @onSaved="saveTreatment"
        />
    </v-dialog>
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import {
    SavedBaseTreatmentComponent,
    Treatment,
    TreatmentComponent,
    TreatmentComponentSetupConfig, TreatmentFormConfig,
    treatmentsAPI, TreatmentSetupConfig,
} from "@/services/treatments/treatmentsAPI";
import TreatmentComponentChip from "@/components/Treatments/TreatmentComponentChip.vue";
import TreatmentComponentForm from "@/components/Treatments/TreatmentComponentForm.vue";
import TreatmentsFileUploadForm from "@/components/Treatments/TreatmentsFileUploadForm.vue";
import { useAuth } from "@/lib/ts/auth";
import { withLoadingScreen } from "@/lib/loading";
import TreatmentForm from "@/components/Treatments/TreatmentForm.vue";

const { experimentCode, treatments, experimentSetupStatus } = defineProps<{
    experimentCode: string;
    treatments: Treatment[];
    experimentSetupStatus: string;
}>();

const emit = defineEmits<{
    (e: "treatmentSaved"): void;
}>();

onMounted(async () => {
    await withLoadingScreen(async () => {
        treatmentFormConfig.value = await treatmentsAPI.fetchTreatmentFormConfig();
    });
});

const auth = useAuth();

const userCanCreateTreatments = computed(() => {
   return auth.isAuthorized("Treatments:Create") && experimentSetupStatus !== "Final";
});
const userCanEditTreatments = computed(() => {
    return auth.isAuthorized("Treatments:Update");
});
const userCanDeleteTreatments = computed(() => {
    return auth.isAuthorized("Treatments:Delete") && experimentSetupStatus !== "Final";
});

const isTreatmentsFileUploadFormVisible = ref<boolean>(false);

const selectedTreatmentComponent = ref<TreatmentComponent | null>(null);
const selectedTreatment = ref<Treatment | undefined>(undefined);

const treatmentFormConfig = ref<TreatmentFormConfig | undefined>(undefined);
const editTreatmentDialogVisible = ref<boolean>(false);
const editTreatmentComponentDialogVisible = ref<boolean>(false);
const treatmentsFile = ref<File | null>(null);

const tableHeaders = ref<VDataTableHeader[]>([
    {
        title: "#",
        value: "number",
        align: "start",
        width: "1%",
        sortable: true,
        cellProps: { style: 'vertical-align: top;', class: 'pt-2' }
    },
    {
        title: "Name",
        value: "name",
        align: "start",
        width: "25%",
        cellProps: { style: 'vertical-align: top;', class: 'pt-2' }
    },
    {
        title: "Type",
        value: "type.displayName",
        align: "start",
        width: "25%",
        cellProps: { style: 'vertical-align: top;', class: 'pt-2' }
    },
    {
        title: "Actives",
        value: "actives",
        align: "start",
        width: "25%",
        cellProps: { style: 'vertical-align: top;' }
    },
    {
        title: "Inactives",
        value: "inactives",
        align: "start",
        width: "25%",
        cellProps: { style: 'vertical-align: top;' }
    },
]);

if (userCanEditTreatments || userCanDeleteTreatments) {
    tableHeaders.value.push(
        {
            title: "Actions",
            value: "actions",
            align: "start",
            width: "5%",
            cellProps: { style: 'vertical-align: top;' }
        }
    );
}

function showTreatmentsFileUploadForm() {
    isTreatmentsFileUploadFormVisible.value = true;
}

async function saveTreatmentsSetupFile() {
    if (!treatmentsFile.value) {
        return;
    }
    await withLoadingScreen(async() => {
        const formData = new FormData();
        formData.append("treatments-setup-file", treatmentsFile.value!);
        await treatmentsAPI.createTreatmentsFromSetupFile(experimentCode, formData);
        treatmentsFile.value = null;
        emit("treatmentSaved");
        closeFileUpload();
    })
}

function closeFileUpload() {
    treatmentsFile.value = null;
    isTreatmentsFileUploadFormVisible.value = false;
}

function addTreatment() {
    selectedTreatment.value = undefined;
    editTreatmentDialogVisible.value = true;
}

function editTreatment(treatment: Treatment) {
    selectedTreatment.value = treatment;
    editTreatmentDialogVisible.value = true;
}

async function deleteTreatment(treatment: Treatment) {
    if (confirm("Are you sure you want to delete this treatment?")) {
        await withLoadingScreen(async () => {
            await treatmentsAPI.deleteTreatment(experimentCode, treatment.id);
            emit("treatmentSaved");
        });
    }
}

async function deleteAllTreatments() {
    if (confirm(`Are you sure you want to delete all ${treatments.length} treatments from ${experimentCode}?`)) {
        await withLoadingScreen(async () => {
            await treatmentsAPI.deleteAllTreatmentsForExperimentCode(experimentCode);
            emit("treatmentSaved");
        });
    }
}

function editTreatmentComponent(treatmentComponent: TreatmentComponent): void {
    selectedTreatmentComponent.value = treatmentComponent;
    editTreatmentComponentDialogVisible.value = true;
}

async function saveTreatment(treatment: TreatmentSetupConfig, treatmentComponentIdsToDelete: string[]): Promise<void> {
    await withLoadingScreen(async () => {
       if ("id" in treatment) {
           const id = treatment.id as string;
           await treatmentsAPI.updateTreatment(experimentCode, id, treatment, treatmentComponentIdsToDelete);
       } else {
           await treatmentsAPI.createTreatment(experimentCode, treatment);
       }
       editTreatmentDialogVisible.value = false;
       emit("treatmentSaved");
    });
}

async function saveTreatmentComponent(treatmentComponent: TreatmentComponentSetupConfig) {
    await withLoadingScreen(async () => {
        if ("id" in treatmentComponent) {
            await treatmentsAPI.updateTreatmentComponent({
                ...treatmentComponent,
            } as unknown as SavedBaseTreatmentComponent);
        }
    });
    editTreatmentComponentDialogVisible.value = false;
    emit("treatmentSaved");
}

function getRowProps(item: { item?: Treatment }) {
    const treatment = item.item!;

    if (isNegativeControl(treatment)) {
        return { class: "negative-control" };
    }
    if (isPositiveControl(treatment)) {
        return { class: "positive-control" };
    }
    return {};
}

function isNegativeControl(treatment: Treatment): boolean {
    return treatment.type.displayName.toLowerCase().includes("negative control");
}

function isPositiveControl(treatment: Treatment): boolean {
    return treatment.type.displayName.toLowerCase().includes("positive control");
}

</script>

<style scoped>
:deep(tr.negative-control > td) {
    background-color: #c2ebc2 !important;
}
:deep(tr.positive-control > td) {
    background-color: #ffc7cd !important;
}
</style>
